import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import {
  HttpClient,
  HttpContext,
  HttpErrorResponse,
  HttpParams,
} from '@angular/common/http';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import {
  makeStateKey,
  StateKey,
  TransferState,
} from '@angular/platform-browser';
import { Params } from '@angular/router';
import { Observable, of, throwError } from 'rxjs';
import { map } from 'rxjs/operators';
import { ZodError } from 'zod';

import {
  FacilitiesError,
  FacilitiesResponse,
  FooterResponseSchema,
  HeaderResponseSchema,
} from '@digital-platform/rs-cms/domain';
import {
  CmsPageResponse,
  CmsPageError,
  HeaderResponse,
  HeaderError,
  ChannelError,
  FooterResponse,
  FooterError,
} from '@digital-platform/rs-cms/domain';
import { USE_GLOBAL_SPINNER } from '@digital-platform/rs-shared/services';
import { ChannelsType } from '../interfaces/cms-interfaces';
import { Materials } from '../interfaces/materials.model';

@Injectable({
  providedIn: 'root',
})
export class CmsContentService {
  private httpClient = inject(HttpClient);
  private transferState = inject(TransferState);
  private platformId = inject(PLATFORM_ID);

  /**
   * getPageAsJson
   * This makes a call to the cms to return page content as a JSON-formatted set
   * of configuration data meant to be fed into the Dynamic Content Outlet to render
   * page content
   *
   * @returns observable containing page content fetched from the CMS
   */
  public getPageAsJson(
    path: string,
    queryParams?: Params
  ): Observable<CmsPageResponse> {
    const params = new HttpParams({
      fromObject: {
        ...queryParams,
        path,
        _format: 'json',
      },
    });

    const pageContentKey = makeStateKey<CmsPageResponse>(
      `CMS_PAGE_${path}_${params}`
    );
    if (
      isPlatformBrowser(this.platformId) &&
      this.transferState.hasKey(pageContentKey)
    ) {
      const pageContent = this.transferState.get(pageContentKey, {
        pageContent: [],
      });
      // remove the key after 15 seconds rather than immediately, to prevent double-loading
      // of content on initial page load
      setTimeout(() => {
        console.log(`Removing ${pageContentKey} from transfer state`);
        this.transferState.remove(pageContentKey);
      }, 15000);
      return of(pageContent);
    } else {
      return this.httpClient
        .get<string>('/api/v3/entity/url_alias', {
          params,
          context: new HttpContext().set(USE_GLOBAL_SPINNER, false),
        })
        .pipe(
          map((data: any) => {
            try {
              if (data.message === 'No entity was found') {
                // This means it's a V1 page. No need to throw an error, just return here.
                return;
              }
              const content = CmsPageResponse.parse(data);
              if (isPlatformServer(this.platformId)) {
                this.transferState.set(pageContentKey, content);
              }
              return content;
            } catch (e) {
              // throw 404 errors up the chain to be handled by the caller
              if (e instanceof HttpErrorResponse && e.status === 404) {
                return throwError(() => e);
              }
              return this.handleError(data, e, CmsPageError);
            }
          })
        );
    }
  }

  public getHeaderContent(): Observable<HeaderResponse> {
    const headerContentKey = makeStateKey<HeaderResponse>('CMS_HEADER_CONTENT');

    if (
      isPlatformBrowser(this.platformId) &&
      this.transferState.hasKey(headerContentKey)
    ) {
      const headerContent = this.transferState.get(headerContentKey, []);
      this.transferState.remove(headerContentKey);
      return of(headerContent);
    } else {
      return this.httpClient
        .get<{ data: HeaderResponse[] }>('/api/v3/navbar', {
          context: new HttpContext().set(USE_GLOBAL_SPINNER, false),
        })
        .pipe(
          map(({ data }) => {
            try {
              const content = HeaderResponseSchema.parse(data);
              if (isPlatformServer(this.platformId)) {
                this.transferState.set(headerContentKey, content);
              }
              return content;
            } catch (e) {
              return this.handleError(data, e, HeaderError);
            }
          })
        );
    }
  }

  public getFooterContent(): Observable<FooterResponse> {
    const footerContentKey = makeStateKey<FooterResponse>('CMS_FOOTER_CONTENT');

    const params = new HttpParams({
      fromObject: {
        _format: 'json',
      },
    });

    if (
      isPlatformBrowser(this.platformId) &&
      this.transferState.hasKey(footerContentKey)
    ) {
      const footerContent = this.transferState.get(footerContentKey, []);
      this.transferState.remove(footerContentKey);
      return of(footerContent);
    } else {
      return this.httpClient
        .get('/api/v3/footers', {
          params,
          context: new HttpContext().set(USE_GLOBAL_SPINNER, false),
        })
        .pipe(
          map((data) => {
            try {
              const content = FooterResponseSchema.parse(data);
              if (isPlatformServer(this.platformId)) {
                this.transferState.set(footerContentKey, content);
              }

              return content;
            } catch (e) {
              return this.handleError(data, e, FooterError);
            }
          })
        );
    }
  }

  public getFacilitiesContent(
    stateCode?: string
  ): Observable<FacilitiesResponse> {
    const facilitiesContentKey = makeStateKey<FacilitiesResponse>(
      'CMS_FACILITIES_CONTENT'
    );

    const params = new HttpParams({
      fromObject: {
        _format: 'json',
      },
    });

    if (
      isPlatformBrowser(this.platformId) &&
      this.transferState.hasKey(facilitiesContentKey)
    ) {
      const facilitiesContent = this.transferState.get(
        facilitiesContentKey,
        []
      );
      this.transferState.remove(facilitiesContentKey);

      return of(facilitiesContent);
    } else {
      let endpoint = '/api/v3/views/es_facilities';
      if (stateCode) {
        endpoint += `/${stateCode}`;
      }

      return this.httpClient
        .get(endpoint, {
          params,
          context: new HttpContext().set(USE_GLOBAL_SPINNER, true),
        })
        .pipe(
          map((data: any) => {
            try {
              if (data.message === 'No entity was found') {
                // This means it's a V1 page. No need to throw an error, just return here.
                return;
              }
              const content = FacilitiesResponse.parse(data);
              if (isPlatformServer(this.platformId)) {
                this.transferState.set(facilitiesContentKey, content);
              }

              return content;
            } catch (e) {
              return this.handleError(data, e, FacilitiesError);
            }
          })
        );
    }
  }

  private handleError(data: any, e: any, errorType: any): any {
    let errorMessage = '';
    const pageError = errorType.safeParse(data);

    if (pageError.success) {
      errorMessage = pageError.data.message;
      // this causes errors in SSR
      // } else if (ErrorEvent !== undefined && e instanceof ErrorEvent) {
      //   // HTTP Error
      //   errorMessage = e.error.message;
    } else if (e instanceof ZodError) {
      // Zod Parse error
      errorMessage = e.message;
    }

    throw errorMessage ? new Error(errorMessage) : e;
  }

  getShopProductImage(categoryCode: string): Observable<Materials> {
    return this.httpClient
      .get(`cms/api/v3/entity/field/product_category/code/${categoryCode}`)
      .pipe(map((response: any) => response as Materials));
  }

  getBannerAlerts() {
    const bannerAlertsKey = makeStateKey<ChannelsType[]>('CMS_BANNER_ALERTS');

    const params = new HttpParams({
      fromObject: {
        _format: 'json',
      },
    });

    const transferredValue = this.getFromTransferState(bannerAlertsKey);

    if (transferredValue) {
      return of(transferredValue);
    } else {
      return this.httpClient
        .get<ChannelsType[]>('/api/v2/outageMessages', {
          params,
          context: new HttpContext().set(USE_GLOBAL_SPINNER, false),
        }) // update to appropriate environment
        .pipe(
          map((data) => {
            try {
              this.setInTransferState(bannerAlertsKey, data);
              return data;
            } catch (e) {
              return this.handleError(data, e, ChannelError);
            }
          })
        );
    }
  }

  private setInTransferState<T>(key: StateKey<T>, value: T): void {
    if (isPlatformServer(this.platformId)) {
      this.transferState.set(key, value);
    }
  }

  private getFromTransferState<T>(key: StateKey<T>): T | null {
    if (isPlatformBrowser(this.platformId) && this.transferState.hasKey(key)) {
      const value = this.transferState.get(key, null);
      this.transferState.remove(key);

      return value;
    }

    return null;
  }
}
